import { useEffect } from 'react'

import type { Analysis, ReportProgress } from '@/modules/analysis'
import { REPORT_STATUSES, useAnalysisActions } from '@/modules/analysis'
import { useWebsocketContext } from '@/modules/websocketContext'
import { recordWebsocketEvent } from '@/services/monitoring'
import type {
  CaseFactsStore,
  Check,
  Insights,
  InsightsStore,
  Project,
  ProjectStore,
  Statement,
  Transaction,
} from '../stores'
import type { CaseFactDTO } from '../stores/types'
import { useCaseFactsActions } from './caseFacts'
import { useInsightsActions } from './insights'
import { useProject, useProjectActions, useProjectLoaders } from './project'

type Actions = ProjectStore['actions'] &
  InsightsStore['actions'] &
  Pick<CaseFactsStore['actions'], 'setCaseFacts'>

interface EventData {
  action: WebsocketAction
  project: Project
  statements: Statement[]
  statement: Statement
  transactions: Transaction[]
  transaction: Transaction
  checks: Check[]
  analysis: Analysis
  progress: ReportProgress
  insights: Insights
  facts: CaseFactDTO[]
}

const HANDLERS = {
  UPDATE_PROJECT: (data: EventData, { setProject }: Actions) =>
    setProject(data.project),
  FETCH_STATEMENTS: (data: EventData, { setStatements }: Actions) =>
    setStatements(data.statements),
  CREATE_STATEMENT: (data: EventData, { addStatement }: Actions) =>
    addStatement(data.statement),
  DELETE_STATEMENT: (data: EventData, { deleteStatements }: Actions) =>
    deleteStatements([data.statement.id]),
  UPDATE_STATEMENT: (data: EventData, { setStatement }: Actions) =>
    setStatement(data.statement),
  CREATE_STATEMENT_TRANSACTIONS: (
    data: EventData,
    { addTransactions }: Actions,
  ) => addTransactions(data.transactions),
  CREATE_STATEMENT_CHECKS: (data: EventData, { addChecks }: Actions) =>
    addChecks(data.checks),
  UPDATE_TRANSACTION: (data: EventData, { setTransaction }: Actions) =>
    setTransaction(data.transaction),
  UPDATE_MANY_TRANSACTIONS: (
    data: EventData,
    { updateTransactions }: Actions,
  ) => updateTransactions(data.transactions),
  UPDATE_PROMPT: (data: EventData, { setAnalysis }: any) =>
    setAnalysis(data.analysis),
  CREATE_PROMPT: (data: EventData, { setAnalysis }: any) =>
    setAnalysis(data.analysis),
  DELETE_PROMPT: (_data: EventData, { setAnalysisStatus }: any) =>
    setAnalysisStatus(REPORT_STATUSES.notStarted),
  PROMPT_PROGRESS: (data: EventData, { setAnalysisProgress }: any) =>
    setAnalysisProgress(data.progress),
  CREATE_INSIGHTS: (data: EventData, { setInsights }: Actions) =>
    setInsights(data.insights),
  UPDATE_INSIGHTS: (data: EventData, { setInsights }: Actions) =>
    setInsights(data.insights),
  UPDATE_CASE_FACTS: (data: EventData, { setCaseFacts }: Actions) =>
    setCaseFacts(data.facts),
}

type WebsocketAction = keyof typeof HANDLERS

export const useWebsocket = () => {
  const project = useProject()
  const { isReady, subscribe, unsubscribe } = useWebsocketContext()
  const projectActions = useProjectActions()
  const analysisActions = useAnalysisActions()
  const insightsActions = useInsightsActions()
  const { setCaseFacts } = useCaseFactsActions()
  const { fetchProject, fetchStatements } = useProjectLoaders()

  const actions = {
    ...projectActions,
    ...analysisActions,
    ...insightsActions,
    setCaseFacts,
  }

  useEffect(() => {
    const slug = project?.slug
    if (!isReady || !slug) return

    const subscription = subscribe(
      { channel: 'ProjectChannel', slug },
      {
        connected: (reconnected) => {
          if (!reconnected) return
          fetchProject(slug)
          fetchStatements(slug)
        },
        received: (data: unknown) => {
          const eventData = data as EventData
          const handler = HANDLERS[eventData.action]
          if (!handler) return

          recordWebsocketEvent('ProjectChannel', eventData)
          handler(eventData, actions)
        },
      },
    )
    return () => unsubscribe(subscription)
    // `subscribe` is memoized on the consumer, so depending on it re-runs this
    // effect when a token refresh swaps the consumer. Without it the channel
    // stays bound to the discarded consumer and live updates go silent.
    // `actions` is deliberately omitted: it is rebuilt every render and would
    // re-subscribe on each one.
  }, [isReady, project?.slug, subscribe, unsubscribe])
}
